import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { GET_APP_TOKENS_QUERY_KEY } from './useGetAppTokens';

import AppTokensService, {
  AppToken,
  UpdateActiveDto,
} from '@/services/AppTokensService';

export const UPDATE_APP_TOKEN_MUTATION_KEY = ['updateAppToken'];

export function useUpdateAppTokens() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: UPDATE_APP_TOKEN_MUTATION_KEY,

    mutationFn: ({ id, update }: { id: number; update: UpdateActiveDto }) =>
      AppTokensService.updateAppToken(id, update),

    onMutate: async ({ id, update }) => {
      const previousData = queryClient.getQueryData<AppToken[]>(
        GET_APP_TOKENS_QUERY_KEY,
      );

      queryClient.setQueryData<AppToken[]>(
        GET_APP_TOKENS_QUERY_KEY,
        produce((draft) => {
          if (!draft) return;
          const tokenToUpdate = draft.find((item) => item.id === id);
          if (tokenToUpdate) {
            tokenToUpdate.active = update.active;
          }
        }),
      );

      return { previousData };
    },

    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          GET_APP_TOKENS_QUERY_KEY,
          context.previousData,
        );
      }
    },

    onSuccess: (updatedToken) => {
      queryClient.setQueryData<AppToken[]>(
        GET_APP_TOKENS_QUERY_KEY,
        (oldTokens) =>
          oldTokens?.map((token) =>
            token.id === updatedToken.id ? updatedToken : token,
          ) ?? [],
      );
    },
  });

  return {
    updateAppToken: mutateAsync,
    isLoading: isPending,
  };
}
